import type { ApiResource } from "../../shared/api/resource.js";

export interface BacktestDeletionTarget {
  readonly id: string;
  readonly botId: string;
  readonly label: string;
}

export interface FilteredDeletionPlan {
  readonly targets: readonly BacktestDeletionTarget[];
  readonly activeCount: number;
  readonly totalCount: number;
}

/** Fail closed on malformed pages, rather than interpreting an unknown status as failed. */
export function filteredDeletionPlan(runs: readonly ApiResource[]): FilteredDeletionPlan {
  const seen = new Set<string>();
  const targets: BacktestDeletionTarget[] = [];
  let activeCount = 0;
  for (const run of runs) {
    if (run.id.trim() === "" || seen.has(run.id)) {
      throw new Error(
        "Backtest preview contains a missing or duplicate run ID. Refresh and try again.",
      );
    }
    seen.add(run.id);
    if (run.status === "queued" || run.status === "running") {
      activeCount += 1;
    } else if (
      run.status === "completed" ||
      run.status === "failed" ||
      run.status === "cancelled"
    ) {
      targets.push({
        id: run.id,
        botId: typeof run.botId === "string" ? run.botId : "",
        label: typeof run.botName === "string" ? run.botName : "Backtest",
      });
    } else {
      throw new Error(
        "Backtest preview contains an unknown run status. No results will be deleted.",
      );
    }
  }
  return { targets, activeCount, totalCount: runs.length };
}

export interface FilteredDeletionResult {
  readonly deleted: readonly BacktestDeletionTarget[];
  readonly failed: readonly { target: BacktestDeletionTarget; reason: string }[];
}

/** Only the explicitly confirmed IDs are deleted, once each, without automatic retries. */
export async function deleteFrozenBacktests(
  targets: readonly BacktestDeletionTarget[],
  remove: (id: string) => Promise<unknown>,
  onProgress: (processed: number) => void,
): Promise<FilteredDeletionResult> {
  const deleted: BacktestDeletionTarget[] = [];
  const failed: { target: BacktestDeletionTarget; reason: string }[] = [];
  for (const target of targets) {
    try {
      await remove(target.id);
      deleted.push(target);
    } catch (error) {
      failed.push({
        target,
        reason:
          error instanceof Error ? error.message : "Deletion failed. Refresh to verify the result.",
      });
    }
    onProgress(deleted.length + failed.length);
  }
  return { deleted, failed };
}
